// File: src/components/admin/FansTab.js

import React, { useState, useEffect, useMemo } from 'react';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import app from '../../services/firebaseConfig';
import UserProfilePicture from '../profile/UserProfilePicture';
import FanLeaderboardModal from '../fans/FanLeaderboard';
import ArtistFansModal from '../fans/ArtistFansModal';
import LeaderboardScoringModal from '../fans/LeaderboardScoringModal';
import { useNavigate } from 'react-router-dom';

import { BADGE_TIERS } from '../../config/constants';

const db = getFirestore(app);

const FansTab = () => {
    const [viewToggle, setViewToggle] = useState('artists'); // 'artists' or 'fans'
    const [timeFrame, setTimeFrame] = useState('this_month'); // 'this_month' or 'all_time'
    
    const [loading, setLoading] = useState(true);
    const [isScoringInfoOpen, setIsScoringInfoOpen] = useState(false);
    const [leaderboardCache, setLeaderboardCache] = useState({ 
        topArtists: [], 
        topArtistsAllTime: [],
        topArtistsThisMonth: [],
        topFans: [], 
        topFansAllTime: [], 
        topFansThisMonth: [] 
    });
    const [activeModalArtistId, setActiveModalArtistId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedArtist, setSelectedArtist] = useState(null);
    const navigate = useNavigate();

    // Memoized Artist List
    const displayedArtists = useMemo(() => {
        if (timeFrame === 'this_month') {
            return leaderboardCache.topArtistsThisMonth?.length > 0 
                ? leaderboardCache.topArtistsThisMonth 
                : leaderboardCache.topArtistsAllTime || leaderboardCache.topArtists || [];
        }
        return leaderboardCache.topArtistsAllTime || leaderboardCache.topArtists || [];
    }, [timeFrame, leaderboardCache]);

    // 🌟 Memoized Fan List (Filters out any Owner accounts automatically)
    const displayedFans = useMemo(() => {
        const rawList = timeFrame === 'this_month' 
            ? (leaderboardCache.topFansThisMonth?.length > 0 ? leaderboardCache.topFansThisMonth : leaderboardCache.topFansAllTime || leaderboardCache.topFans || [])
            : (leaderboardCache.topFansAllTime || leaderboardCache.topFans || []);

        return rawList.filter((fan) => {
            const isOwner = fan.isOwner === true || (fan.userId && fan.artistId && fan.userId === fan.artistId);
            return !isOwner; // Exclude owner accounts from the public/admin Top Fans tab
        });
    }, [timeFrame, leaderboardCache]);

    const handleOpenFans = (artist) => {
        setSelectedArtist(artist);
        setIsModalOpen(true);
    };

    const handleArtistClick = (e, artistId) => {
        e.stopPropagation();
        if (artistId) {
            navigate(`/profile/${artistId}`);
        }
    };

    useEffect(() => {
        const cacheDocRef = doc(db, 'fan_leaderboards', 'fan_hub_v1');
        
        const unsubscribe = onSnapshot(cacheDocRef, (snapshot) => {
            if (snapshot.exists()) {
                setLeaderboardCache(snapshot.data());
            }
            setLoading(false);
        }, (error) => {
            console.error("Error streaming global cache snapshot:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    return (
        <div className="p-6 md:p-8 space-y-6">
            <div className="max-w-3xl mx-auto space-y-6">
                
                {/* IDENTITY HEADER */}
                <header className="text-center space-y-4">
                    <div>
                        <h2 className="text-2xl font-black uppercase tracking-tight text-slate-900 dark:text-white">
                            Global Fan Hub Leaderboard
                        </h2>
                        {leaderboardCache.lastUpdated && (
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-1">
                                Daily Batch Snapshot: {leaderboardCache.lastUpdated.toDate().toLocaleDateString('en-AU', { hour: '2-digit', minute:'2-digit' })}
                            </p>
                        )}
                    </div>

                    {/* DUAL SLIDER CONTROLS */}
                    <div className="flex flex-col items-center justify-center gap-3">
                        
                        {/* 1. PRIMARY TOGGLE */}
                        <div className="bg-slate-100 dark:bg-slate-900 p-1 rounded-2xl flex gap-1 h-12 items-center shadow-inner w-full max-w-xs border border-slate-200 dark:border-slate-800">
                            <button 
                                onClick={() => setViewToggle('artists')}
                                className={`flex-1 h-10 text-xs font-black uppercase tracking-wider rounded-xl transition-all duration-150 ${
                                    viewToggle === 'artists' ? 'bg-[#ff00cc] text-white shadow-md scale-[1.02]' : 'text-slate-500 dark:text-slate-400'
                                }`}
                            >
                                Top Artists
                            </button>
                            <button 
                                onClick={() => setViewToggle('fans')}
                                className={`flex-1 h-10 text-xs font-black uppercase tracking-wider rounded-xl transition-all duration-150 ${
                                    viewToggle === 'fans' ? 'bg-[#ff00cc] text-white shadow-md scale-[1.02]' : 'text-slate-500 dark:text-slate-400'
                                }`}
                            >
                                Top Fans
                            </button>
                        </div>

                        {/* 2. SECONDARY PILL */}
                        <div className="bg-slate-100/70 dark:bg-slate-900/60 p-0.5 rounded-xl flex gap-1 h-8 items-center max-w-[210px] border border-slate-200/80 dark:border-slate-800/80">
                            <button 
                                onClick={() => setTimeFrame('this_month')}
                                className={`flex-1 h-7 px-3 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all duration-150 ${
                                    timeFrame === 'this_month' 
                                        ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 shadow-xs' 
                                        : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                                }`}
                            >
                                This Month
                            </button>
                            <button 
                                onClick={() => setTimeFrame('all_time')}
                                className={`flex-1 h-7 px-3 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all duration-150 ${
                                    timeFrame === 'all_time' 
                                        ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 shadow-xs' 
                                        : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                                }`}
                            >
                                All-Time
                            </button>
                        </div>

                        {/* 🌟 Friendly Info Button */}
                        <button
                            type="button"
                            onClick={() => setIsScoringInfoOpen(true)}
                            aria-label="Explain how scoring works"
                            className="w-6 h-6 rounded-full border border-slate-300 dark:border-slate-700 flex items-center justify-center text-[10px] font-black text-slate-400 hover:text-[#ff00cc] hover:border-[#ff00cc] transition-colors"
                        >
                            i
                        </button>
                    </div>
                </header>

                {/* LIST DISPLAY */}
                {loading ? (
                    <div className="text-center py-20 text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">
                        Loading global hub snapshot...
                    </div>
                ) : (
                    <div className="space-y-2.5">
                        
                        {/* VIEW A: TOP PLATFORM CREATORS */}
                        {viewToggle === 'artists' && (
                            displayedArtists.length === 0 ? (
                                <div className="text-center py-12 text-sm text-slate-400 font-bold">
                                    No creator point activity recorded for this period.
                                </div>
                            ) : (
                                displayedArtists.map((artist, idx) => {
                                    const topFanScore = Number(artist.topFanScore) || 0;

                                    // 🌟 Calculate the exact badgeTier so the avatar gets the correct color ring
                                    const resolvedBadgeTier = topFanScore >= BADGE_TIERS.ULTRAFAN.minPoints 
                                        ? 'ULTRAFAN' 
                                        : topFanScore >= BADGE_TIERS.SUPAFAN.minPoints 
                                            ? 'SUPAFAN' 
                                            : 'FAN';

                                    return (
                                        <div key={artist.id || idx} className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800/60 shadow-2sm">
                                            <div className="flex items-center gap-3">
                                                <span className="w-5 font-black text-xs text-slate-400 tabular-nums text-center">{idx + 1}</span>
                                                <div 
                                                    onClick={(e) => handleArtistClick(e, artist.id)} 
                                                    className="cursor-pointer hover:scale-105 transition-transform"
                                                >
                                                    <UserProfilePicture photoURL={artist.avatar} sizeClass="w-10 h-10" />
                                                </div>
                                                <div>
                                                    <h4 className="font-black text-sm text-slate-800 dark:text-slate-200 leading-tight">{artist.artistName}</h4>
                                                    
                                                    {/* 🌟 Updated subtext: uniformly displays "Points Earned" */}
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mt-0.5">
                                                        {(Number(artist.score) || 0).toLocaleString()} Points Earned
                                                    </p>
                                                </div>
                                            </div>

                                            <div 
                                                onClick={() => handleOpenFans(artist)}
                                                className="flex flex-col items-center text-center justify-center cursor-pointer hover:opacity-80 transition-opacity min-w-[85px] ml-auto"
                                            >
                                                <span className="text-[10px] font-black uppercase tracking-wider mb-1 block w-full leading-[1.15]">
                                                    {topFanScore >= BADGE_TIERS.ULTRAFAN.minPoints ? (
                                                        <span className="flex flex-col items-center">
                                                            <span className="text-[#ff00cc] text-[9px]">Top</span>
                                                            <span className="text-[#ff00cc]">UltraFan</span>
                                                        </span>
                                                    ) : topFanScore >= BADGE_TIERS.SUPAFAN.minPoints ? (
                                                        <span className="flex flex-col items-center">
                                                            <span className="text-[#3b82f6] text-[9px]">Top</span>
                                                            <span className="text-[#3b82f6]">SupaFan</span>
                                                        </span>
                                                    ) : (
                                                        <span className="text-[#22c55e]">Top Fan</span>
                                                    )}
                                                </span>

                                                {/* 🌟 Passes badgeTier directly: Standard Fans get a green border, UltraFans get pink */}
                                                <UserProfilePicture 
                                                    photoURL={artist.topFanAvatar} 
                                                    sizeClass="w-10 h-10" 
                                                    badgeTier={resolvedBadgeTier}
                                                />
                                            </div>
                                        </div>
                                    );
                                })
                            )
                        )}

                        {/* VIEW B: TOP PLATFORM SUPPORTERS */}
                        {viewToggle === 'fans' && (
                            displayedFans.length === 0 ? (
                                <div className="text-center py-12 text-sm text-slate-400 font-bold">
                                    No supporter point activity recorded for this period.
                                </div>
                            ) : (
                                displayedFans.map((fan, idx) => {
                                    const handleFanClick = (e) => {
                                        e.stopPropagation();
                                        if (fan.userId) {
                                            navigate(`/profile/${fan.userId}`);
                                        }
                                    };

                                    const handleArtistSubClick = (e) => {
                                        e.stopPropagation();
                                        if (fan.artistId) {
                                            navigate(`/profile/${fan.artistId}`);
                                        }
                                    };

                                    const fanScore = Number(fan.score) || Number(fan.totalPointsTipped) || 0;
                                    const resolvedArtistAvatar = fan.artistAvatar || fan.avatar || "";

                                    const resolvedFanTier = fanScore >= BADGE_TIERS.ULTRAFAN.minPoints 
                                        ? 'ULTRAFAN' 
                                        : fanScore >= BADGE_TIERS.SUPAFAN.minPoints 
                                            ? 'SUPAFAN' 
                                            : 'FAN';
                                    
                                    return (
                                        <div key={fan.id || idx} className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800/60 shadow-2sm">
                                            
                                            <div className="flex items-center gap-3">
                                                <span className="w-5 font-black text-xs text-slate-400 tabular-nums text-center">{idx + 1}</span>
                                                
                                                <div onClick={handleFanClick} className="cursor-pointer hover:scale-105 transition-transform flex-shrink-0">
                                                    <UserProfilePicture 
                                                        photoURL={fan.fanAvatar} 
                                                        sizeClass="w-10 h-10" 
                                                        badgeTier={resolvedFanTier}
                                                    />
                                                </div>

                                                <div className="space-y-0.5">
                                                    <h4 
                                                        onClick={handleFanClick}
                                                        className="font-black text-sm text-slate-800 dark:text-slate-200 leading-tight cursor-pointer hover:text-[#ff00cc] transition-colors"
                                                    >
                                                        {fan.fanName || "Anonymous Fan"}
                                                    </h4>
                                                    
                                                    <p className="text-[10px] font-black uppercase tracking-wider block leading-none">
                                                        {resolvedFanTier === 'ULTRAFAN' ? (
                                                            <span className="text-[#ff00cc]">UltraFan</span>
                                                        ) : resolvedFanTier === 'SUPAFAN' ? (
                                                            <span className="text-[#3b82f6]">SupaFan</span>
                                                        ) : (
                                                            <span className="text-[#22c55e]">Fan</span>
                                                        )}
                                                    </p>
                                                    
                                                    <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider block leading-none tabular-nums">
                                                        {fanScore.toLocaleString()} Points Tipped
                                                    </p>
                                                </div>
                                            </div>

                                            {/* ARTIST IDENTIFIER MODULE */}
                                            <div 
                                                onClick={handleArtistSubClick}
                                                className="flex flex-col items-center justify-center cursor-pointer hover:opacity-80 transition-opacity min-w-[90px] ml-auto"
                                            >
                                                <span className="text-[10px] font-black uppercase tracking-wider mb-1.5 text-center block w-full leading-tight text-slate-700 dark:text-slate-300 max-w-[85px] truncate">
                                                    {fan.artistName || "Creator"}
                                                </span>

                                                <UserProfilePicture 
                                                    photoURL={resolvedArtistAvatar} 
                                                    sizeClass="w-10 h-10" 
                                                />
                                            </div>

                                        </div>
                                    );
                                })
                            )
                        )}

                    </div>
                )}
            </div>

            {/* OVERLAY MODALS */}
            {activeModalArtistId && (
                <FanLeaderboardModal 
                    artistId={activeModalArtistId} 
                    isOpen={true} 
                    onClose={() => setActiveModalArtistId(null)} 
                />
            )}

            <ArtistFansModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                artistId={selectedArtist?.id}
                artistName={selectedArtist?.artistName}
            />

            <LeaderboardScoringModal 
                isOpen={isScoringInfoOpen}
                onClose={() => setIsScoringInfoOpen(false)}
                isDarkMode={document.documentElement.classList.contains('dark')}
            />
        </div>
    );
};

export default FansTab;